import React, { FC } from 'react';
import styled from 'styled-components';
import ClearIcon from '@mui/icons-material/Clear';
import DoneIcon from '@mui/icons-material/Done';

import { buttonVariants } from '@clatter/ui';
import { NgButton } from '../NgButton/NgButton';

interface FormButtonsProps {
  isLoading: boolean;
  isDirty: boolean;
  isValid: boolean;
  reset: () => void;
  /** Override the submit button — changes it to type="button" with this onClick */
  onSubmitClick?: () => void;
  /** Push buttons to the bottom of the nearest flex-column container */
  pushToBottom?: boolean;
  submitLabel?: string;
  resetLabel?: string;
  /** Alignment of buttons. Defaults to "center"; "right" puts both buttons on the right side */
  placement?: 'center' | 'right';
  /** When provided, overrides the internal !isDirty || !isValid disabled calculation. */
  submitDisabled?: boolean;
}

const StyledButtonsContainer = styled.div<{ $pushToBottom?: boolean; $placement: 'center' | 'right' }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $placement }) => ($placement === 'right' ? 'flex-end' : 'center')};
  gap: 8px;

  .left-side,
  .right-side {
    flex: ${({ $placement }) => ($placement === 'right' ? '0 0 auto' : '1 1 auto')};
    display: flex;
    justify-content: center;
    gap: 15px;
  }

  ${({ $pushToBottom }) => $pushToBottom && `margin-top: auto;`}
`;

export const FormButtons: FC<FormButtonsProps> = ({
  isLoading,
  isDirty,
  isValid,
  reset,
  onSubmitClick,
  pushToBottom,
  submitLabel = 'Submit',
  resetLabel = 'Reset',
  placement = 'center',
  submitDisabled,
}) => {
  return (
    <StyledButtonsContainer $pushToBottom={pushToBottom} $placement={placement}>
      <div className="left-side">
        <NgButton
          disabled={isLoading || !isDirty}
          icon={ClearIcon}
          variant={buttonVariants.clear}
          onClick={() => reset()}
          type="reset"
          testId="dynamic-form-reset-button"
        >
          {resetLabel}
        </NgButton>
      </div>
      <div className="right-side">
        <NgButton
          isLoading={isLoading}
          disabled={submitDisabled !== undefined ? submitDisabled : (!isDirty || !isValid)}
          icon={DoneIcon}
          type={onSubmitClick ? 'button' : 'submit'}
          variant={buttonVariants.primary}
          testId="dynamic-form-submit-button"
          onClick={onSubmitClick}
        >
          {submitLabel}
        </NgButton>
      </div>
    </StyledButtonsContainer>
  );
};
